import React, { useState, useRef, useCallback, useEffect } from "react";
import * as XLSX from "xlsx";

const t = () => new Proxy({}, { get: (_, p) => (window as any).__ENGPORTAL_THEME?.[p] }) as any;

const CANVAS_W = 576;
const CANVAS_H = 384;
const PRINT_W_IN = 6;
const PRINT_H_IN = 4;
const PADDING = 20;
const LINE_GAP = 4;

interface LabelField {
  id: string;
  title: string;
  value: string;
  x: number;
  y: number;
  width: number;
  height: number;
  fontSize: number;
  fontWeight: string;
}

interface LabelState {
  fields: LabelField[];
  logoX: number;
  logoY: number;
  logoW: number;
  logoH: number;
  qrX: number;
  qrY: number;
  qrSize: number;
  showLogo: boolean;
}

// Simple QR Code generator using canvas - generates data URL
const generateQR = (text: string, size: number): Promise<string> => {
  return new Promise((resolve) => {
    // We'll use a simple QR encoding via an external API rendered to image
    // For offline: encode data into a grid pattern
    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext("2d");
    if (!ctx) { resolve(""); return; }

    // Use QR code API image
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      ctx.drawImage(img, 0, 0, size, size);
      resolve(canvas.toDataURL());
    };
    img.onerror = () => {
      // Fallback: draw a placeholder
      ctx.fillStyle = "#000";
      ctx.fillRect(0, 0, size, size);
      ctx.fillStyle = "#fff";
      ctx.fillRect(4, 4, size - 8, size - 8);
      ctx.fillStyle = "#000";
      ctx.font = `${Math.max(8, size / 8)}px monospace`;
      ctx.textAlign = "center";
      ctx.fillText("QR", size / 2, size / 2 + 4);
      resolve(canvas.toDataURL());
    };
    img.src = `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(text)}&format=png`;
  });
};

// Measure text width using canvas
const measureText = (text: string, fontSize: number, fontWeight: string): number => {
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) return text.length * fontSize * 0.6;
  ctx.font = `${fontWeight} ${fontSize}px 'IBM Plex Sans', Arial, sans-serif`;
  return ctx.measureText(text).width;
};

const autoLayout = (fields: LabelField[], logoH: number, qrSize: number, customFontSize?: number): LabelField[] => {
  if (fields.length === 0) return fields;

  const startY = PADDING + logoH + 16;
  const availH = CANVAS_H - startY - PADDING;
  const availW = CANVAS_W - PADDING * 2 - qrSize - 20;

  // Smart font sizing: scale up for fewer fields, down for more
  let fontSize: number;
  if (customFontSize) {
    fontSize = customFontSize;
  } else {
    // Base: use as much vertical space as possible
    // Each line needs fontSize + gap, so max fontSize = (availH / numFields) - gap
    const maxByHeight = Math.floor((availH / fields.length) - LINE_GAP);
    // Also constrain by width - find longest text and ensure it fits
    const testSize = Math.min(28, Math.max(10, maxByHeight));
    const maxTextW = Math.max(...fields.map(f => measureText(f.title + ": " + f.value, testSize, "600")));
    const scaleByWidth = maxTextW > availW ? (availW / maxTextW) * testSize : testSize;
    fontSize = Math.round(Math.min(28, Math.max(10, scaleByWidth)));
  }

  const lineHeight = fontSize + Math.max(LINE_GAP, fontSize * 0.35);
  const blockH = fields.length * lineHeight;
  const topOffset = startY + Math.max(0, (availH - blockH) / 2);

  // Left-align all fields
  const blockLeft = PADDING;

  return fields.map((f, i) => ({
    ...f,
    x: blockLeft,
    y: topOffset + i * lineHeight,
    fontSize,
    fontWeight: "600",
    width: measureText(f.title + ": " + f.value, fontSize, "600") + 10,
    height: lineHeight,
  }));
};

export default function LabelDesignerPage() {
  const theme = t();
  const canvasRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [label, setLabel] = useState<LabelState>({
    fields: [], logoX: PADDING, logoY: PADDING, logoW: 160, logoH: 48,
    qrX: CANVAS_W - PADDING - 100, qrY: CANVAS_H - PADDING - 100, qrSize: 100, showLogo: true,
  });
  const [qrDataUrl, setQrDataUrl] = useState<string>("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [dragging, setDragging] = useState<{ target: string; offX: number; offY: number } | null>(null);
  const [fileName, setFileName] = useState<string>("");
  const [hasHeader, setHasHeader] = useState<boolean>(true);
  const [savedLabels, setSavedLabels] = useState<{ name: string; state: LabelState }[]>(() => {
    try { return JSON.parse(localStorage.getItem("engportal-labels-v2") || "[]"); } catch { return []; }
  });
  const [labelName, setLabelName] = useState("Untitled Label");

  const selected = label.fields.find(f => f.id === selectedId);

  // Generate QR whenever fields change
  useEffect(() => {
    if (label.fields.length === 0) { setQrDataUrl(""); return; }
    const qrText = label.fields.map(f => `${f.title}: ${f.value}`).join("\n");
    generateQR(qrText, label.qrSize * 2).then(setQrDataUrl);
  }, [label.fields, label.qrSize]);

  const processFile = (fileData: ArrayBuffer, name: string, headerRow: boolean) => {
    try {
      const data = new Uint8Array(fileData);
      const wb = XLSX.read(data, { type: "array" });
      const ws = wb.Sheets[wb.SheetNames[0]];

      let fields: LabelField[];

      if (headerRow) {
        // First row is header - use as column names
        const rows: any[] = XLSX.utils.sheet_to_json(ws, { defval: "" });
        const headers = Object.keys(rows[0] || {});
        const titleCol = headers.find(h => /title|header|field|name|label/i.test(h)) || headers[0];
        const valueCol = headers.find(h => /value|data|content|result/i.test(h)) || headers[1];

        if (!titleCol || !valueCol) {
          alert("Could not find Title and Value columns. Please use columns named 'Title' and 'Value'.");
          return;
        }

        fields = rows
          .filter(r => r[valueCol] !== "" && r[valueCol] !== null && r[valueCol] !== undefined)
          .map((r, i) => ({
            id: `field-${i}`,
            title: String(r[titleCol] || ""),
            value: String(r[valueCol] || ""),
            x: PADDING, y: PADDING + 48 + i * 20,
            width: 200, height: 20, fontSize: 13, fontWeight: "600",
          }));
      } else {
        // No header row - treat columns A and B as Title and Value directly
        const rows: any[][] = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
        fields = rows
          .filter(r => r[0] !== "" && r[0] !== null && r[1] !== "" && r[1] !== null && r[1] !== undefined)
          .map((r, i) => ({
            id: `field-${i}`,
            title: String(r[0] || ""),
            value: String(r[1] || ""),
            x: PADDING, y: PADDING + 48 + i * 20,
            width: 200, height: 20, fontSize: 13, fontWeight: "600",
          }));
      }

      if (fields.length === 0) {
        alert("No rows with data in both columns found.");
        return;
      }

      const laid = autoLayout(fields, label.showLogo ? label.logoH : 0, label.qrSize);
      setLabel(prev => ({
        ...prev,
        fields: laid,
        qrX: CANVAS_W - PADDING - prev.qrSize,
        qrY: CANVAS_H - PADDING - prev.qrSize,
      }));
      setSelectedId(null);
      setLabelName(name.replace(/\.[^.]+$/, ""));
    } catch (err) {
      alert("Error reading file. Please ensure it is a valid .xlsx or .csv file.");
    }
  };

  const lastFileData = useRef<{ data: ArrayBuffer; name: string } | null>(null);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (evt) => {
      const buf = evt.target?.result as ArrayBuffer;
      lastFileData.current = { data: buf.slice(0), name: file.name };
      processFile(buf, file.name, hasHeader);
    };
    reader.readAsArrayBuffer(file);
    e.target.value = "";
  };

  const handleMouseDown = (e: React.MouseEvent, target: string) => {
    e.stopPropagation();
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;

    let itemX = 0, itemY = 0;
    if (target === "logo") { itemX = label.logoX; itemY = label.logoY; }
    else if (target === "qr") { itemX = label.qrX; itemY = label.qrY; }
    else {
      const f = label.fields.find(f => f.id === target);
      if (f) { itemX = f.x; itemY = f.y; }
    }

    setDragging({ target, offX: e.clientX - rect.left - itemX, offY: e.clientY - rect.top - itemY });
    setSelectedId(target === "logo" || target === "qr" ? null : target);
  };

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!dragging || !canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(e.clientX - rect.left - dragging.offX, CANVAS_W - 20));
    const y = Math.max(0, Math.min(e.clientY - rect.top - dragging.offY, CANVAS_H - 20));

    if (dragging.target === "logo") {
      setLabel(prev => ({ ...prev, logoX: x, logoY: y }));
    } else if (dragging.target === "qr") {
      setLabel(prev => ({ ...prev, qrX: x, qrY: y }));
    } else {
      setLabel(prev => ({
        ...prev,
        fields: prev.fields.map(f => f.id === dragging.target ? { ...f, x, y } : f),
      }));
    }
  }, [dragging]);

  const handleMouseUp = useCallback(() => setDragging(null), []);

  const updateField = (id: string, updates: Partial<LabelField>) => {
    setLabel(prev => ({ ...prev, fields: prev.fields.map(f => f.id === id ? { ...f, ...updates } : f) }));
  };

  const reAutoLayout = () => {
    setLabel(prev => ({
      ...prev,
      fields: autoLayout(prev.fields, prev.showLogo ? prev.logoH : 0, prev.qrSize),
    }));
  };

  const saveLabel = () => {
    const updated = [...savedLabels.filter(l => l.name !== labelName), { name: labelName, state: label }];
    setSavedLabels(updated);
    localStorage.setItem("engportal-labels-v2", JSON.stringify(updated));
  };

  const loadLabel = (saved: { name: string; state: LabelState }) => {
    setLabelName(saved.name);
    setLabel(saved.state);
    setSelectedId(null);
  };

  const deleteSavedLabel = (name: string) => {
    const updated = savedLabels.filter(l => l.name !== name);
    setSavedLabels(updated);
    localStorage.setItem("engportal-labels-v2", JSON.stringify(updated));
  };

  const handlePrint = () => {
    const pw = window.open("", "_blank");
    if (!pw) return;
    const sx = (PRINT_W_IN * 96) / CANVAS_W;
    const sy = (PRINT_H_IN * 96) / CANVAS_H;

    let html = "";
    if (label.showLogo) {
      html += `<img src="/img/logo-sidebar.png" style="position:absolute;left:${label.logoX * sx}px;top:${label.logoY * sy}px;width:${label.logoW * sx}px;height:${label.logoH * sy}px;object-fit:contain;" />`;
    }
    label.fields.forEach(f => {
      const fs = f.fontSize * sx;
      html += `<div style="position:absolute;left:${f.x * sx}px;top:${f.y * sy}px;font-size:${fs}px;font-weight:${f.fontWeight};font-family:'IBM Plex Sans',Arial,sans-serif;white-space:nowrap;"><span style="color:#666">${f.title}:</span> <span style="color:#000">${f.value}</span></div>`;
    });
    if (qrDataUrl) {
      html += `<img src="${qrDataUrl}" style="position:absolute;left:${label.qrX * sx}px;top:${label.qrY * sy}px;width:${label.qrSize * sx}px;height:${label.qrSize * sy}px;" />`;
    }

    pw.document.write(`<!DOCTYPE html><html><head><title>Print Label</title>
      <link href="https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&display=swap" rel="stylesheet">
      <style>@page{size:${PRINT_W_IN}in ${PRINT_H_IN}in;margin:0}body{margin:0}.label{position:relative;width:${PRINT_W_IN}in;height:${PRINT_H_IN}in;overflow:hidden;font-family:'IBM Plex Sans',Arial,sans-serif}</style>
    </head><body><div class="label">${html}</div>
    <script>setTimeout(()=>{window.print();window.close()},600)<\/script></body></html>`);
    pw.document.close();
  };

  const btnStyle = (color: string): React.CSSProperties => ({ padding: "6px 14px", borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: "pointer", border: `1px solid ${color}33`, background: color + "15", color });

  return (
    <div style={{ display: "flex", height: "100%", gap: 16, color: theme.text }}>
      {/* Main area */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 12 }}>
        {/* Top bar */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: theme.text }}>Label Designer</h2>
            <span style={{ color: theme.textDim, fontSize: 10, background: theme.bg, padding: "3px 8px", borderRadius: 4 }}>4" × 6"</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={handleFileUpload}
              style={{ display: "none" }}
            />
            <button onClick={() => fileInputRef.current?.click()} style={{ ...btnStyle(theme.accent), display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ fontSize: 14 }}>📄</span> Upload Excel
            </button>
            {label.fields.length > 0 && (
              <>
                <button onClick={reAutoLayout} style={btnStyle(theme.purple || "#8B5CF6")}>Auto-Align</button>
                <button onClick={handlePrint} style={btnStyle(theme.green)}>Print</button>
                <button onClick={saveLabel} style={btnStyle(theme.accent)}>Save</button>
              </>
            )}
            <button onClick={() => { setLabel(prev => ({ ...prev, fields: [] })); setSelectedId(null); setFileName(""); }} style={btnStyle(theme.textDim)}>Clear</button>
          </div>
        </div>

        {fileName && <div style={{ color: theme.textDim, fontSize: 11 }}>Loaded: <strong style={{ color: theme.text }}>{fileName}</strong> — {label.fields.length} fields</div>}

        {/* Label name */}
        {label.fields.length > 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ color: theme.textDim, fontSize: 11 }}>Label Name:</span>
            <input value={labelName} onChange={e => setLabelName(e.target.value)} style={{ background: theme.bg, border: `1px solid ${theme.border}`, borderRadius: 6, padding: "5px 10px", color: theme.text, fontSize: 12, fontWeight: 600, width: 200 }} />
          </div>
        )}

        {/* Canvas */}
        <div style={{ flex: 1, display: "flex", justifyContent: "center", alignItems: "center", overflow: "auto" }}>
          {label.fields.length === 0 ? (
            <div
              onClick={() => fileInputRef.current?.click()}
              style={{
                width: CANVAS_W, height: CANVAS_H, background: "#FFFFFF", border: `2px dashed ${theme.border}`, borderRadius: 8,
                display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 16, cursor: "pointer",
                boxShadow: "0 4px 24px rgba(0,0,0,0.1)", transition: "border-color 0.2s",
              }}
              onMouseEnter={e => (e.currentTarget.style.borderColor = theme.accent)}
              onMouseLeave={e => (e.currentTarget.style.borderColor = theme.border)}
            >
              <div style={{ fontSize: 48, opacity: 0.3 }}>📄</div>
              <div style={{ color: "#666", fontSize: 14, fontWeight: 600 }}>Upload an Excel file to design your label</div>
              <div style={{ color: "#999", fontSize: 11, maxWidth: 340, textAlign: "center", lineHeight: 1.5 }}>
                Excel should have two columns: <strong>Title</strong> (e.g. Lot #, Qty, Mfg Date) and <strong>Value</strong> (the data for this label). Only rows with values will appear.
              </div>
              <div style={{ ...btnStyle(theme.accent), marginTop: 8, fontSize: 13, padding: "8px 20px" } as any}>Choose File</div>
            </div>
          ) : (
            <div
              ref={canvasRef}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              onClick={e => { if (e.target === e.currentTarget) setSelectedId(null); }}
              style={{
                width: CANVAS_W, height: CANVAS_H, position: "relative",
                background: "#FFFFFF", border: `1px solid ${theme.border}`, borderRadius: 4,
                boxShadow: "0 4px 24px rgba(0,0,0,0.15)", cursor: dragging ? "grabbing" : "default",
              }}
            >
              {/* Logo */}
              {label.showLogo && (
                <img
                  src="/img/logo-sidebar.png"
                  alt="AvCarb"
                  onMouseDown={e => handleMouseDown(e, "logo")}
                  style={{
                    position: "absolute", left: label.logoX, top: label.logoY,
                    width: label.logoW, height: label.logoH, objectFit: "contain",
                    cursor: "grab", border: "1px solid transparent",
                  }}
                  onMouseEnter={e => (e.currentTarget.style.border = "1px dashed #3B82F6")}
                  onMouseLeave={e => (e.currentTarget.style.border = "1px solid transparent")}
                />
              )}

              {/* Fields */}
              {label.fields.map(f => (
                <div
                  key={f.id}
                  onMouseDown={e => handleMouseDown(e, f.id)}
                  style={{
                    position: "absolute", left: f.x, top: f.y,
                    fontSize: f.fontSize, fontWeight: f.fontWeight,
                    fontFamily: "'IBM Plex Sans', Arial, sans-serif",
                    cursor: "grab", userSelect: "none",
                    whiteSpace: "nowrap",
                    padding: "1px 3px",
                    borderRadius: 2,
                    border: selectedId === f.id ? "1.5px solid #3B82F6" : "1px solid transparent",
                    background: selectedId === f.id ? "rgba(59,130,246,0.06)" : "transparent",
                  }}
                  onMouseEnter={e => { if (selectedId !== f.id) e.currentTarget.style.border = "1px dashed #93C5FD"; }}
                  onMouseLeave={e => { if (selectedId !== f.id) e.currentTarget.style.border = "1px solid transparent"; }}
                >
                  <span style={{ color: "#888" }}>{f.title}:</span>{" "}
                  <span style={{ color: "#000" }}>{f.value}</span>
                </div>
              ))}

              {/* QR Code */}
              {qrDataUrl && (
                <img
                  src={qrDataUrl}
                  alt="QR"
                  onMouseDown={e => handleMouseDown(e, "qr")}
                  style={{
                    position: "absolute", left: label.qrX, top: label.qrY,
                    width: label.qrSize, height: label.qrSize,
                    cursor: "grab", border: "1px solid transparent", borderRadius: 2,
                  }}
                  onMouseEnter={e => (e.currentTarget.style.border = "1px dashed #3B82F6")}
                  onMouseLeave={e => (e.currentTarget.style.border = "1px solid transparent")}
                />
              )}
            </div>
          )}
        </div>
      </div>

      {/* Right panel: Properties + Saved */}
      <div style={{ width: 210, flexShrink: 0, display: "flex", flexDirection: "column", gap: 12, overflow: "auto" }}>
        {/* Options */}
        <div style={{ background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: 8, padding: 12 }}>
          <h4 style={{ margin: "0 0 8px 0", fontSize: 12, fontWeight: 700, color: theme.text }}>Options</h4>
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: theme.textMuted, cursor: "pointer" }}>
            <input type="checkbox" checked={hasHeader} onChange={e => {
              setHasHeader(e.target.checked);
              if (lastFileData.current) processFile(lastFileData.current.data, lastFileData.current.name, e.target.checked);
            }} />
            File has header row
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: theme.textMuted, cursor: "pointer", marginTop: 4 }}>
            <input type="checkbox" checked={label.showLogo} onChange={e => setLabel(prev => ({ ...prev, showLogo: e.target.checked }))} />
            Show AvCarb Logo
          </label>
          <div style={{ marginTop: 8 }}>
            <span style={{ color: theme.textDim, fontSize: 10 }}>QR Size: {label.qrSize}px</span>
            <input type="range" min={60} max={160} value={label.qrSize} onChange={e => setLabel(prev => ({ ...prev, qrSize: Number(e.target.value) }))} style={{ width: "100%", marginTop: 2 }} />
          </div>
          {label.fields.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <span style={{ color: theme.textDim, fontSize: 10 }}>Text Size: {label.fields[0]?.fontSize || 13}px</span>
              <input type="range" min={8} max={32} value={label.fields[0]?.fontSize || 13} onChange={e => {
                const fs = Number(e.target.value);
                setLabel(prev => ({ ...prev, fields: autoLayout(prev.fields, prev.showLogo ? prev.logoH : 0, prev.qrSize, fs) }));
              }} style={{ width: "100%", marginTop: 2 }} />
            </div>
          )}
          {label.fields.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <span style={{ color: theme.textDim, fontSize: 10 }}>Logo Size: {label.logoW}px</span>
              <input type="range" min={60} max={280} value={label.logoW} onChange={e => {
                const w = Number(e.target.value);
                setLabel(prev => ({ ...prev, logoW: w, logoH: Math.round(w * 0.3) }));
              }} style={{ width: "100%", marginTop: 2 }} />
            </div>
          )}
        </div>

        {/* Field properties */}
        {selected && (
          <div style={{ background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: 8, padding: 12 }}>
            <h4 style={{ margin: "0 0 8px 0", fontSize: 12, fontWeight: 700, color: theme.text }}>Edit Field</h4>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <div>
                <span style={{ color: theme.textDim, fontSize: 10 }}>Title</span>
                <input value={selected.title} onChange={e => updateField(selected.id, { title: e.target.value })} style={{ width: "100%", background: theme.bg, border: `1px solid ${theme.border}`, borderRadius: 4, padding: "4px 8px", color: theme.text, fontSize: 11, marginTop: 2 }} />
              </div>
              <div>
                <span style={{ color: theme.textDim, fontSize: 10 }}>Value</span>
                <input value={selected.value} onChange={e => updateField(selected.id, { value: e.target.value })} style={{ width: "100%", background: theme.bg, border: `1px solid ${theme.border}`, borderRadius: 4, padding: "4px 8px", color: theme.text, fontSize: 11, marginTop: 2 }} />
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                <div style={{ flex: 1 }}>
                  <span style={{ color: theme.textDim, fontSize: 10 }}>Font Size</span>
                  <input type="number" value={selected.fontSize} onChange={e => updateField(selected.id, { fontSize: Number(e.target.value) })} style={{ width: "100%", background: theme.bg, border: `1px solid ${theme.border}`, borderRadius: 4, padding: "4px 6px", color: theme.text, fontSize: 11, marginTop: 2 }} />
                </div>
                <div style={{ flex: 1 }}>
                  <span style={{ color: theme.textDim, fontSize: 10 }}>Weight</span>
                  <select value={selected.fontWeight} onChange={e => updateField(selected.id, { fontWeight: e.target.value })} style={{ width: "100%", background: theme.bg, border: `1px solid ${theme.border}`, borderRadius: 4, padding: "4px 6px", color: theme.text, fontSize: 11, marginTop: 2 }}>
                    <option value="400">Normal</option>
                    <option value="600">Semi-Bold</option>
                    <option value="700">Bold</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Saved labels */}
        <div style={{ background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: 8, padding: 12 }}>
          <h4 style={{ margin: "0 0 8px 0", fontSize: 12, fontWeight: 700, color: theme.text }}>Saved Labels</h4>
          {savedLabels.length === 0 && <div style={{ color: theme.textDim, fontSize: 10 }}>No saved labels yet</div>}
          {savedLabels.map(l => (
            <div key={l.name} style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 4 }}>
              <button onClick={() => loadLabel(l)} style={{ flex: 1, textAlign: "left", padding: "5px 8px", borderRadius: 4, fontSize: 10, background: theme.bg, border: `1px solid ${theme.border}`, color: theme.text, cursor: "pointer", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{l.name}</button>
              <button onClick={() => deleteSavedLabel(l.name)} style={{ padding: "4px 6px", borderRadius: 4, fontSize: 9, background: theme.red + "12", color: theme.red, border: `1px solid ${theme.red}33`, cursor: "pointer" }}>×</button>
            </div>
          ))}
        </div>

        {/* Instructions */}
        <div style={{ background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: 8, padding: 12 }}>
          <h4 style={{ margin: "0 0 6px 0", fontSize: 12, fontWeight: 700, color: theme.text }}>How to Use</h4>
          <div style={{ color: theme.textDim, fontSize: 10, lineHeight: 1.6 }}>
            1. Upload an Excel with two columns (Title + Value)<br />
            2. Uncheck 'File has header row' if no headers<br />
            3. Only rows with data in both columns appear<br />
            4. Fields auto-align on the label<br />
            5. Drag fields, logo, or QR to adjust<br />
            6. Click a field to edit text/size<br />
            7. Use <strong>Auto-Align</strong> to reset layout<br />
            8. <strong>Print</strong> outputs at 4"×6"
          </div>
        </div>
      </div>
    </div>
  );
}
